import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Select from "react-select";
import { Box, TextField } from "@mui/material";
import countryList from "react-select-country-list";
import { updateCompanyDetails, fetchVenueById } from "../utils/api";
import PageLayout from "../layout/PageLayout";
import "../styles.css";
import BackButton from "../components/BackButton";

const BillingDetailsPage = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const { userData, opportunityId, venueId } = state || {};
  const partnerLogoUrl = state?.partnerLogoUrl || (typeof window !== 'undefined' ? sessionStorage.getItem('partnerLogoUrl') || undefined : undefined);
  const customUi = state?.customUi ?? (typeof window !== 'undefined' ? sessionStorage.getItem('customUi') === 'true' : undefined);
  const storedRmsName = state?.venueRmsName || (typeof window !== 'undefined' ? (sessionStorage.getItem('venueRmsName') || undefined) : undefined);

  const [formData, setFormData] = useState({
    contactFirstName: "",
    contactLastName: "",
    email: "",
    name: userData?.company?.name || "",
    vat: userData?.company?.vat || "",
    address: userData?.company?.address || "",
    city: userData?.company?.city || "",
    postalCode: userData?.company?.postalCode || "",
    countryCode: userData?.company?.country || "",
  });

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [isLibroRms, setIsLibroRms] = useState(storedRmsName === "Libro");

  const countryOptions = countryList().getData();

  useEffect(() => {
    if (storedRmsName) {
      setIsLibroRms(storedRmsName === "Libro");
      return;
    }
    let cancelled = false;
    (async () => {
      if (!venueId) return;
      try {
        const venue = await fetchVenueById(venueId);
        if (!cancelled) {
          const rmsName = venue?.rms?.name || "";
          setIsLibroRms(rmsName === "Libro");
          try { sessionStorage.setItem('venueRmsName', rmsName || ""); } catch (_) {}
        }
      } catch (_) {
        // Non-blocking; default label remains VAT Number
      }
    })();
    return () => { cancelled = true; };
  }, [venueId, storedRmsName]);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleCountryChange = (option) => {
    setFormData({
      ...formData,
      countryCode: option.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      await updateCompanyDetails(userData.company.id, formData);

      const updatedUserData = {
        ...userData,
        name: formData.contactFirstName || userData?.name,
        lastName: formData.contactLastName || userData?.lastName,
        email: formData.email || userData?.email,
        company: {
          ...userData.company,
          ...formData,
        },
      };

      navigate("/payment", {
        state: {
          userData: updatedUserData,
          opportunityId,
          venueId,
          type: state.type,
          partnerLogoUrl,
          customUi,
        },
      });
    } catch (err) {
      setError("Failed to update billing details. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageLayout type={state?.type} wide partnerLogoUrl={partnerLogoUrl} customUi={customUi}>

      {error && <div className="error-message">{error}</div>}

      <form onSubmit={handleSubmit} className="rwg-form">
        <div className="billing-columns">
          <div className="billing-col">
            <h3>Contact Details</h3>
            <p>This is the person legally confirming the subscription on behalf of the business.</p>
            <Box mt={1}>
              <TextField
                label="First Name"
                name="contactFirstName"
                value={formData.contactFirstName}
                onChange={handleInputChange}
                fullWidth
                size="small"
                required
                variant="outlined"
                margin="dense"
              />
              <TextField
                label="Last Name"
                name="contactLastName"
                value={formData.contactLastName}
                onChange={handleInputChange}
                fullWidth
                size="small"
                required
                variant="outlined"
                margin="dense"
              />
              <TextField
                label="Email"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                fullWidth
                size="small"
                required
                variant="outlined"
                margin="dense"
                helperText="This email will be used for the subscription (billing, receipts, notifications)."
              />
            </Box>
          </div>
          <div className="billing-col">
            <h3>Company / Invoice Details</h3>
            <p>These details will appear on your invoices and must match your legal entity.</p>
            <Box mt={1}>
              <TextField
                label="Business Name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                fullWidth
                size="small"
                required
                variant="outlined"
                margin="dense"
              />
              <TextField
                label={isLibroRms ? "GST Number" : "VAT Number"}
                name="vat"
                value={formData.vat}
                onChange={handleInputChange}
                fullWidth
                size="small"
                required
                variant="outlined"
                margin="dense"
              />
              <TextField
                label="Address"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                fullWidth
                size="small"
                required
                variant="outlined"
                margin="dense"
              />
              <div className="field-row">
                <div>
                  <TextField
                    label="City"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    fullWidth
                    size="small"
                    required
                    variant="outlined"
                    margin="dense"
                  />
                </div>
                <div>
                  <TextField
                    label="Postal Code"
                    name="postalCode"
                    value={formData.postalCode}
                    onChange={handleInputChange}
                    fullWidth
                    size="small"
                    required
                    variant="outlined"
                    margin="dense"
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Country *</label>
                <Select
                  options={countryOptions}
                  value={countryOptions.find(
                    (option) => option.value === formData.countryCode
                  )}
                  onChange={handleCountryChange}
                  required
                />
              </div>
            </Box>
          </div>
        </div>
        <div className="billing-actions">
          <BackButton />
          <button type="submit" className="button" disabled={loading}>
            {loading ? "Updating..." : "Continue to Payment"}
          </button>
        </div>
      </form>
    </PageLayout>
  );
};

export default BillingDetailsPage;
